import { Suspense, lazy, useCallback, useMemo } from 'react';
import { Alert, Box, Grid, Typography, styled } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import DirectionsBoat from '@material-design-icons/svg/filled/directions_boat_filled.svg?react';
import { Map } from 'leaflet';
import moment from 'moment-timezone';
import { useSafetyPredictionServiceGetMooredVessels } from '@/api/ui/queries';
import { MooredVessel, SafetyPrediction } from '@/api/ui/requests';
import MarkerSvg from '@/assets/svg/marker.svg?react';
import OperationalView from '@/assets/svg/operational.svg?react';
import ApiErrorAlert from '@/components/feedback/Error/ApiErrorAlert';
import LoadingIndicator from '@/components/feedback/LoadingIndicator';
import NoScrollBox from '@/components/layout/NoScrollBox/NoScrollBox';
import { MapMarkerProps } from '@/components/maps/Map/types';
import useToBerthSafetyNavigate from '@/features/SafetyPrediction/BerthDashboard/useToBerthSafetyNavigate';
import { OVERVIEW_MAP_PREDICTION_DAYS_IN_ADVANCE } from '@/features/SafetyPrediction/Overview/types';
import { useGraphConfig } from '@/hooks/useGraphConfig';
import useMapParams from '@/hooks/useMapParams';
import useOrganisation from '@/hooks/useOrganisation';
import usePageTitle from '@/hooks/usePageTitle';
import { SafetyPredictionTriggerStaticValues } from '@/types';

const VesselThresholdIcon = styled(MarkerSvg)(({ color, theme }) => ({
  'circle:nth-of-type(1)': {
    fill: color ?? theme.palette?.below_lower_limit?.main,
  },
  height: '24px',
}));

function DisplaySafetyWarning({
  safetyPrediction: { forecast, trigger, percentage },
  berthTimezone,
}: {
  safetyPrediction: SafetyPrediction;
  berthTimezone?: string;
}) {
  const { getDefaultGraphConfig } = useGraphConfig();

  return (
    <Grid item xs={12}>
      <Typography variant={'bodySemiBoldSmall'} component={'span'}>
        Safety assessed at:&nbsp;
        {moment(forecast.timestamp).tz(berthTimezone).format('DD-MM-YYYY HH:mm z')}
        <br />
      </Typography>

      <Typography variant={'bodySemiBoldSmall'} component={'span'}>
        Warning: &ge;&nbsp;{getDefaultGraphConfig(trigger)?.defaultLabel} ({percentage.toFixed(2)}%)
      </Typography>
      <br />
      {SafetyPredictionTriggerStaticValues[trigger]?.name && (
        <Typography variant={'bodySemiBoldSmall'} component={'span'}>
          Caused by: &nbsp;{SafetyPredictionTriggerStaticValues[trigger]?.name}
        </Typography>
      )}
    </Grid>
  );
}

function WarningPopUp({
  mooredVessel: { mostCriticalSafetyPrediction, berthTimezones, vessel, arrivalTime, berthName, customerVesselDefinition },
}: {
  mooredVessel: MooredVessel;
}) {
  return (
    <Grid container spacing={1}>
      <Grid item xs={2}>
        <VesselThresholdIcon color={mostCriticalSafetyPrediction?.threshold?.color} />
      </Grid>
      <Grid item xs={10}>
        <Typography variant={'bodySemiBoldMedium'}>{vessel.name}</Typography>
        <br />
        <Typography variant={'bodySemiBoldSmall'}>
          {vessel.vesselType} - {customerVesselDefinition?.customerVesselClass ?? vessel.vesselClass}
        </Typography>
        <br />
        <Typography variant={'bodyRegularSmall'}>{berthName}</Typography>
        <br />
        <Typography variant={'bodyRegularSmall'}>
          Arrived at: {moment(arrivalTime).tz(berthTimezones?.at(0)).format('DD-MM-YYYY HH:mm z')}
        </Typography>
      </Grid>
      {mostCriticalSafetyPrediction && (
        <DisplaySafetyWarning safetyPrediction={mostCriticalSafetyPrediction} berthTimezone={berthTimezones?.at(0)} />
      )}
    </Grid>
  );
}

const OperationalMap = lazy(() => import('@/components/maps/Map/BerthMap'));

function SafetyPredictionOperationalView() {
  // eslint-disable-next-line sonarjs/no-duplicate-string
  usePageTitle('Operational - Map', <OperationalView width={32} key={'Operational - Map'} />);
  const { navigateToBerthSafety } = useToBerthSafetyNavigate();
  const { selectedOrganisationId } = useOrganisation();
  const { defaultPeriod } = useGraphConfig();

  const theme = useTheme();

  const coloredBoatIcon = useCallback(
    (color: string, enabled: boolean) => {
      let fill: string;
      if (enabled) {
        fill = color ?? theme.palette.below_lower_limit.main;
      } else {
        fill = theme.palette.disabled.main;
      }

      return <DirectionsBoat style={{ fill }} />;
    },
    [theme]
  );

  const {
    isError,
    error,
    dataUpdatedAt,
    isLoading,
    data: vessels,
  } = useSafetyPredictionServiceGetMooredVessels(
    {
      xSelectedOrganisationId: selectedOrganisationId,
      dateTime: moment().startOf('minute').toISOString(true),
      predictionDays: OVERVIEW_MAP_PREDICTION_DAYS_IN_ADVANCE,
    },
    [selectedOrganisationId, moment().startOf('minute').toISOString(true)],
    {
      refetchInterval: 60000, // 1 minute
    }
  );

  const { zoomLevel, center, setMapParams } = useMapParams();

  const getRequestTimestamp = useCallback((timezone: string) => moment().tz(timezone), []);

  const navigateToBerth = useCallback(
    (vessel: MooredVessel) => {
      if (!vessel?.availableForSafetyPrediction) return;

      const {
        berthId,
        berthTimezones,
        mostCriticalSafetyPrediction,
        dmaCaseIdentifier,
        vessel: { mmsi },
      } = vessel;

      navigateToBerthSafety({
        berthId,
        loadingCondition: mostCriticalSafetyPrediction?.loadingCondition,
        trigger: mostCriticalSafetyPrediction?.trigger,
        dmaCaseId: dmaCaseIdentifier,
        isOperationalVessel: true,
        vesselDisposition: mostCriticalSafetyPrediction?.vesselDisposition,
        requestTimestamp: getRequestTimestamp(berthTimezones?.at(0)),
        days: defaultPeriod,
        vesselMmsi: mmsi,
      });
    },
    [navigateToBerthSafety, defaultPeriod, getRequestTimestamp]
  );

  const markers: MapMarkerProps[] = useMemo(() => {
    if (!vessels) return [];
    return vessels.map((vessel) => {
      const { mooredPosition, mostCriticalSafetyPrediction, availableForSafetyPrediction } = vessel;
      return {
        latitude: mooredPosition.latitude,
        longitude: mooredPosition.longitude,
        markerIcon: {
          icon: coloredBoatIcon(mostCriticalSafetyPrediction?.threshold?.color, availableForSafetyPrediction),
          iconSize: 27,
          color: mostCriticalSafetyPrediction?.threshold?.color,
          value: mostCriticalSafetyPrediction?.threshold?.percentage,
          enabled: availableForSafetyPrediction,
        },
        popup: {
          content: vessel && <WarningPopUp mooredVessel={vessel} />,
          closeButton: false,
        },
        onMouseOver: (event) => {
          event.target.openPopup();
        },
        onMouseOut: (event) => {
          event.target.closePopup();
        },
        onClick: (_, map: Map) => {
          setMapParams(map);
          navigateToBerth(vessel);
        },
      };
    });
  }, [vessels, coloredBoatIcon, setMapParams, navigateToBerth]);

  if (isError) {
    return <ApiErrorAlert error={error} />;
  }

  if (!isLoading && markers.length === 0) {
    return (
      <Box sx={{ marginX: 'auto', marginY: 10, width: '50vw' }}>
        <Alert severity="warning" color={'warning'}>
          No moored vessels found (yet).
        </Alert>
      </Box>
    );
  }
  if (vessels) {
    return (
      <NoScrollBox component="div">
        <Suspense fallback={<LoadingIndicator message={'Generating data to display vessels on the map. Please be patient...'} />}>
          <OperationalMap mapMarkers={markers} showZoomControl={true} clustering={true} zoomLevel={zoomLevel} center={center} />
          <div>{moment(dataUpdatedAt).format('DD-MM-YYYY HH:mm:ss')}</div>
        </Suspense>
      </NoScrollBox>
    );
  }

  return <LoadingIndicator message={'Loading map with detected vessels...'} />;
}

export default SafetyPredictionOperationalView;
